import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * F7.4 — Componente compartilhado (extraído de ContasPagar.jsx sem alterar comportamento).
 *
 * Props:
 *  - value: string (fornecedor_id atual, "" quando não vinculado)
 *  - valueTexto: string (campo legado `fornecedor` texto livre)
 *  - onSelect(fornecedor): chamado ao escolher (auto-preenche texto)
 *  - onClear(): chamado ao limpar vínculo
 *  - onChangeTexto(txt): chamado ao digitar texto livre
 *  - required: bool (default true)
 *  - inputTestId: string (default "input-fornecedor")
 *  - pickerTestId: string (default "fornecedor-picker")
 *  - labelText: string (default "Fornecedor *")
 */

function formatarCPFCurto(digits) {
  const d = (digits || "").padEnd(11, "").slice(0, 11);
  return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2}).*/, "$1.$2.$3-$4");
}
function formatarCNPJCurto(digits) {
  const d = (digits || "").padEnd(14, "").slice(0, 14);
  return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2}).*/, "$1.$2.$3/$4-$5");
}

export function FornecedorPicker({
  value,
  valueTexto,
  onSelect,
  onClear,
  onChangeTexto,
  required = true,
  inputTestId = "input-fornecedor",
  pickerTestId = "fornecedor-picker",
  labelText = "Fornecedor *",
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedNome, setSelectedNome] = useState("");
  const [selectedTipo, setSelectedTipo] = useState("");
  const [selectedDoc, setSelectedDoc] = useState("");

  useEffect(() => {
    if (!value) {
      setSelectedNome(""); setSelectedTipo(""); setSelectedDoc("");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/fornecedores/${value}`);
        if (cancelled) return;
        setSelectedNome(data.razao_social_nome || "");
        setSelectedTipo(data.tipo_pessoa || "");
        setSelectedDoc(data.documento_normalizado || "");
      } catch {
        // fornecedor pode ter sido desativado; mantém texto legado
      }
    })();
    return () => { cancelled = true; };
  }, [value]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { ativo: true, limit: 20 };
        const q = query.trim();
        if (q) {
          const digits = q.replace(/\D+/g, "");
          if (digits && digits.length >= 3) {
            params.documento = digits;
          } else {
            params.nome = q;
          }
        }
        const { data } = await api.get("/fornecedores", { params });
        if (!cancelled) setResults(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, open]);

  const docFormatado = (f) => {
    if (!f.documento_normalizado) return "";
    return f.tipo_pessoa === "PF"
      ? formatarCPFCurto(f.documento_normalizado)
      : formatarCNPJCurto(f.documento_normalizado);
  };

  const escolher = (f) => { onSelect(f); setOpen(false); setQuery(""); };
  const limpar = () => {
    onClear();
    setSelectedNome(""); setSelectedTipo(""); setSelectedDoc("");
  };

  return (
    <div data-testid={pickerTestId}>
      <div className="flex items-center justify-between mb-1">
        <Label>{labelText}</Label>
        {value && (
          <button type="button" onClick={limpar}
            data-testid={`${pickerTestId}-limpar`}
            className="text-[11px] text-[#D4A652] hover:underline">
            Limpar vínculo
          </button>
        )}
      </div>
      {value ? (
        <div data-testid={`${pickerTestId}-selecionado`}
          className="border border-[#083A6B]/30 bg-[#EAF0EA]/50 rounded-md p-2.5 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-sm text-stone-900 font-medium truncate">
              {selectedNome || valueTexto || "(carregando…)"}
            </div>
            <div className="text-[11px] text-stone-600">
              {selectedTipo && <span className="mr-2">{selectedTipo}</span>}
              {selectedDoc && <span className="font-mono">{
                selectedTipo === "PF" ? formatarCPFCurto(selectedDoc) : formatarCNPJCurto(selectedDoc)
              }</span>}
            </div>
          </div>
          <span className="text-[10px] uppercase tracking-widest text-[#083A6B] font-semibold shrink-0">
            Vinculado
          </span>
        </div>
      ) : (
        <>
          <div className="relative">
            <Input
              data-testid={inputTestId}
              value={valueTexto}
              placeholder="Digite para buscar (nome, fantasia ou documento)"
              onFocus={() => setOpen(true)}
              onChange={(e) => {
                onChangeTexto(e.target.value);
                setQuery(e.target.value);
                setOpen(true);
              }}
              required={required}
            />
            {open && (query.length > 0 || results.length > 0) && (
              <div data-testid={`${pickerTestId}-dropdown`}
                className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-stone-200 rounded-md shadow-lg max-h-64 overflow-auto">
                {loading ? (
                  <div className="p-3 text-xs text-stone-500">Buscando…</div>
                ) : results.length === 0 ? (
                  <div className="p-3 text-xs text-stone-500">
                    Nenhum fornecedor ativo encontrado. Você pode continuar digitando texto livre.
                  </div>
                ) : (
                  <ul>
                    {results.map((f) => (
                      <li key={f.id}>
                        <button type="button"
                          data-testid={`${pickerTestId}-opt-${f.id}`}
                          onClick={() => escolher(f)}
                          className="w-full text-left px-3 py-2 hover:bg-[#EAF0EA] focus:bg-[#EAF0EA] focus:outline-none">
                          <div className="text-sm text-stone-900 flex items-center gap-2">
                            <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-stone-100 text-stone-600">
                              {f.tipo_pessoa}
                            </span>
                            <span className="truncate">{f.razao_social_nome}</span>
                          </div>
                          {(f.nome_fantasia || f.documento_normalizado) && (
                            <div className="text-[11px] text-stone-500 mt-0.5">
                              {f.nome_fantasia && <span className="mr-2">{f.nome_fantasia}</span>}
                              {f.documento_normalizado && (
                                <span className="font-mono">{docFormatado(f)}</span>
                              )}
                            </div>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          {open && (
            <button type="button" onClick={() => setOpen(false)}
              className="fixed inset-0 z-40 cursor-default"
              tabIndex={-1} aria-hidden="true" />
          )}
          <p className="text-[11px] text-stone-500 mt-1">
            Selecione um fornecedor cadastrado para vincular ou continue com texto livre.
          </p>
        </>
      )}
    </div>
  );
}
